import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Palette } from 'lucide-react';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  presets?: string[];
}

const DEFAULT_PRESETS = [
  '#c5b068', // Gold
  '#890c17', // Dark Red
  '#a92226', // Red
  '#2563eb', // Blue
  '#059669', // Green
  '#dc2626', // Red
  '#9333ea', // Purple
  '#ea580c', // Orange
  '#0891b2', // Cyan
  '#65a30d', // Lime
  '#be123c', // Rose
  '#7c3aed', // Violet
];

/**
 * ColorPicker - A color input component with preset colors and visual preview
 */
const ColorPicker: React.FC<ColorPickerProps> = ({ 
  label, 
  value, 
  onChange, 
  presets = DEFAULT_PRESETS 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleColorChange = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const color = event.target.value;
    // Always update the input value to allow typing
    onChange(color);
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const color = event.target.value;
    // Validate and correct the format on blur
    if (!color.startsWith('#')) {
      onChange('#' + color);
    } else if (!color.match(/^#([0-9A-F]{3}){1,2}$/i)) {
      // If invalid hex, revert to previous valid value or default
      if (value.match(/^#([0-9A-F]{3}){1,2}$/i)) {
        // Keep current valid value
        event.target.value = value;
      } else {
        // Set to default black
        onChange('#000000');
      }
    }
  };

  const handlePresetClick = (color: string) => {
    handleColorChange(color);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={`color-${label}`} className="text-sm font-medium">
        {label}
      </Label>
      
      <div className="flex gap-2">
        {/* Color Preview and Input */}
        <div className="flex items-center gap-2 flex-1">
          <div 
            className="w-8 h-8 rounded-md border-2 border-gray-300 cursor-pointer"
            style={{ backgroundColor: value }}
            onClick={() => inputRef.current?.click()}
            title="Click to open colour picker"
          />
          
          <Input
            ref={inputRef}
            type="color"
            value={value}
            onChange={handleInputChange}
            className="w-0 h-0 p-0 border-0 invisible"
          />
          
          <Input
            type="text"
            value={value}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className="flex-1 font-mono text-sm"
            placeholder="#000000"
            maxLength={7}
          />
        </div>
        
        {/* Preset Colors Popover */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="px-3"
              title="Choose from preset colours"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4">
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Preset Colours</h4>
              <div className="grid grid-cols-6 gap-2">
                {presets.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-md border-2 hover:scale-110 transition-transform ${
                      value === color ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => handlePresetClick(color)}
                    title={color}
                  />
                ))}
              </div>
              
              <div className="space-y-2 pt-2 border-t">
                <h5 className="font-medium text-xs text-gray-600">Common Brand Colours</h5>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    className="w-full h-6 rounded text-xs text-white font-medium hover:scale-105 transition-transform"
                    style={{ backgroundColor: '#c5b068' }}
                    onClick={() => handlePresetClick('#c5b068')}
                  >
                    Gold
                  </button>
                  <button
                    className="w-full h-6 rounded text-xs text-white font-medium hover:scale-105 transition-transform"
                    style={{ backgroundColor: '#890c17' }}
                    onClick={() => handlePresetClick('#890c17')}
                  >
                    Red
                  </button>
                  <button
                    className="w-full h-6 rounded text-xs text-white font-medium hover:scale-105 transition-transform"
                    style={{ backgroundColor: '#2563eb' }}
                    onClick={() => handlePresetClick('#2563eb')}
                  >
                    Blue
                  </button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default ColorPicker;